import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import {
  getMealScoreAppearance,
  getMealStatusAppearance,
} from "../../designSystem/chatSemanticColors";
import type {
  MealIngredientItem,
  MealItemDetailsPayload,
} from "./types";
import classes from "./MealItemDetailsCard.module.css";

type Props = {
  data: MealItemDetailsPayload;
};

const MealItemDetailsCard = ({ data }: Props) => {
  const theme = useTheme();
  const [imageSrc, setImageSrc] = React.useState(data.imageUrl);

  React.useEffect(() => {
    setImageSrc(data.imageUrl);
  }, [data.imageUrl]);

  const handleImageError = React.useCallback(() => {
    if (data.fallbackImageUrl && imageSrc !== data.fallbackImageUrl) {
      setImageSrc(data.fallbackImageUrl);
    }
  }, [data.fallbackImageUrl, imageSrc]);

  return (
    <article className={classes.card}>
      <div className={classes.imageWrap}>
        <img
          src={imageSrc}
          alt={data.title}
          className={classes.image}
          loading="lazy"
          onError={handleImageError}
        />
        <div className={classes.imageOverlay}>
          <div className={classes.statusStack}>
            {data.statusTags.map((status) => (
              <span
                key={status}
                className={classes.pill}
                style={getMealStatusAppearance(theme, status)}
              >
                {data.ui.statusLabels[status]}
              </span>
            ))}
          </div>
          {data.score && (
            <div
              className={classes.scoreBadge}
              style={getMealScoreAppearance(theme, data.score.tone)}
            >
              <span className={classes.scoreValue}>{data.score.value}</span>
              <span className={classes.scoreLabel}>{data.score.label}</span>
            </div>
          )}
        </div>
      </div>

      <div className={classes.content}>
        <header>
          <p className={classes.eyebrow}>
            {data.kind === "recipe"
              ? data.ui.kindLabels.recipe
              : data.ui.kindLabels.food}
          </p>
          <h4 className={classes.title}>{data.title}</h4>
          {data.subtitle ? <p className={classes.subtitle}>{data.subtitle}</p> : null}
        </header>

        {data.metadata.length > 0 ? (
          <section className={classes.section}>
            <h5 className={classes.sectionTitle}>
              {data.ui.sectionTitles.overview}
            </h5>
            <div className={classes.metaGrid}>
              {data.metadata.map((item) => (
                <div key={`${item.label}-${item.value}`} className={classes.metaCard}>
                  <span className={classes.metaLabel}>{item.label}</span>
                  <span className={classes.metaValue}>{item.value}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className={classes.section}>
          <h5 className={classes.sectionTitle}>
            {data.ui.sectionTitles.nutritionFacts}
          </h5>
          <div className={classes.nutritionGrid}>
            <NutritionFactCard
              label={data.ui.nutritionFactLabels.calories}
              value={`${data.nutritionFacts.calories}`}
            />
            <NutritionFactCard
              label={data.ui.nutritionFactLabels.protein}
              value={`${data.nutritionFacts.proteinGrams}g`}
            />
            <NutritionFactCard
              label={data.ui.nutritionFactLabels.carbs}
              value={`${data.nutritionFacts.carbsGrams}g`}
            />
            <NutritionFactCard
              label={data.ui.nutritionFactLabels.fat}
              value={`${data.nutritionFacts.fatGrams}g`}
            />
          </div>
          {data.nutritionFacts.micronutrients?.length ? (
            <details className={classes.microDetails}>
              <summary className={classes.microSummary}>
                {data.ui.sectionTitles.micronutrientsToggle}
              </summary>
              <div className={classes.microList}>
                {data.nutritionFacts.micronutrients.map((item) => (
                  <div key={`${item.label}-${item.unit}`} className={classes.microCard}>
                    <span className={classes.microLabel}>{item.label}</span>
                    <span className={classes.microValue}>
                      {item.value}
                      {item.unit}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </section>

        <ItemListSection
          title={
            data.kind === "recipe"
              ? data.ui.sectionTitles.ingredients
              : data.ui.sectionTitles.servingDetails
          }
          items={data.ingredients}
        />

        {data.instructions?.length ? (
          <section className={classes.section}>
            <h5 className={classes.sectionTitle}>
              {data.ui.sectionTitles.instructions}
            </h5>
            <ol className={classes.instructionList}>
              {data.instructions.map((step, index) => (
                <li key={`${index}-${step.slice(0, 24)}`} className={classes.instructionItem}>
                  {step}
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {data.additionalInformation?.length ? (
          <section className={classes.section}>
            <h5 className={classes.sectionTitle}>
              {data.ui.sectionTitles.additionalInformation}
            </h5>
            <div className={classes.infoGrid}>
              {data.additionalInformation.map((item) => (
                <div key={`${item.label}-${item.value}`} className={classes.infoCard}>
                  <span className={classes.infoLabel}>{item.label}</span>
                  <span className={classes.infoValue}>{item.value}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <ItemListSection
          title={data.ui.sectionTitles.groceryList}
          items={data.groceryList}
        />
      </div>
    </article>
  );
};

const NutritionFactCard = (props: { label: string; value: string }) => {
  return (
    <div className={classes.nutritionCard}>
      <span className={classes.nutritionLabel}>{props.label}</span>
      <span className={classes.nutritionValue}>{props.value}</span>
    </div>
  );
};

const ItemListSection = (props: {
  title: string;
  items?: MealIngredientItem[];
}) => {
  if (!props.items?.length) {
    return null;
  }

  return (
    <section className={classes.section}>
      <h5 className={classes.sectionTitle}>{props.title}</h5>
      <ul className={classes.list}>
        {props.items.map((item) => (
          <li key={item.id} className={classes.listItem}>
            <span className={classes.itemName}>{item.name}</span>
            {item.quantity ? (
              <span className={classes.itemQuantity}>{item.quantity}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default MealItemDetailsCard;
